import { BeneficiaryInfo, InsuranceCoverage, OccupationInfo } from "../interfaces/DTO/mislaka_xml_insurence_models";
import { PolicyOrAccount, Product } from "../interfaces/DTO/mislaka_xml_models";
import { MimshakService } from "./mimshakService";
import { PensionFundDetails, PensionFundType, PensionPlanType, RelationType } from "../interfaces/DTO/mislaka_xml_pension_models";

export class MimshakPensionService {
    static isJunkBeneficiary(b: BeneficiaryInfo): boolean {
      if (!!b.id_number && /^0+$/.test(b.id_number)) return true;
      const firstName = b.first_name?.trim() || "";
      const lastName = b.last_name?.trim() || "";
      const isEmptyOrZero = (s: string) => s === "" || s === "0";
      if (isEmptyOrZero(firstName) && isEmptyOrZero(lastName)) return true;
      return false;
    }

    static getBeneficiaryInfo(products: Product[]): BeneficiaryInfo[] {
      return products.flatMap(product =>
        product.policies_or_accounts.flatMap(policy =>
          policy.insurence_coverages?.flatMap(coverage => coverage.beneficiaries) || []
        )
      ).filter(b => !this.isJunkBeneficiary(b));
    }
    static filterInsuranceCandidates = (products: Product[]): Product[] => {
      return products.map((product) => ({
        ...product,
        policies_or_accounts: product.policies_or_accounts.filter((policy) => {
          const hasInsuranceCoverages = policy.insurence_coverages && policy.insurence_coverages.length > 0;
          const hasPensionFundDetails = policy.pension_fund_details && policy.pension_fund_details.length > 0;
          const hasPensionData = policy.pension_data !== undefined && policy.pension_data !== null;
    
          return hasInsuranceCoverages || hasPensionFundDetails || hasPensionData;
        }),
      })).filter((product) => product.policies_or_accounts.length > 0); // מסנן מוצרים שאין בהם פוליסות אחרי הסינון
    };
    
   static getBeneficiaryInfoDistinct(products: Product[]): BeneficiaryInfo[] {
      const allBeneficiaries = this.getBeneficiaryInfo(products);
      const uniqueMap = new Map<string, BeneficiaryInfo>();

      allBeneficiaries.forEach((beneficiary) => { 
        const id = beneficiary.id_number;
        if (!id) return;

        const current = uniqueMap.get(id);

        // אם עדיין לא קיים - פשוט להכניס
        if (!current) {
          uniqueMap.set(id, beneficiary);
        }
        // תמיד להעדיף ערך עם relation_type תקין על פני OTHER, 0, או NULL/UNDEFINED
        else {
          const currentRelation = current.relation_type;
          const beneficiaryRelation = beneficiary.relation_type;

          // בודקים אם הערך הוא לא תקין (NULL/UNDEFINED/0)
          const isCurrentInvalid = currentRelation == null || currentRelation === 0;
          const isBeneficiaryValid = beneficiaryRelation != null && beneficiaryRelation !== 0;

          // מחליפים אם:
          // 1. הקיים הוא NULL/UNDEFINED/0 והחדש יש לו ערך תקין
          // 2. הקיים הוא OTHER והחדש לא OTHER (וגם לא NULL/UNDEFINED/0)
          if (
            (isCurrentInvalid && isBeneficiaryValid) ||
            (currentRelation === RelationType.OTHER &&
             beneficiaryRelation !== RelationType.OTHER &&
             isBeneficiaryValid)
          ) {
            uniqueMap.set(id, beneficiary);
          }
          // אחרת לא נוגעים - שומרים על הקיים:
          // - אם הקיים הוא NON-OTHER והחדש הוא OTHER/NULL/0
          // - אם שניהם NON-OTHER
          // - אם שניהם OTHER או NULL או 0
        }
      });

      return Array.from(uniqueMap.values());
    }
    static getBeneficiaryInfoExtended(products: Product[]): Array<{
        product_type: number;
        policy_number: string;
        policy_name: string;
        beneficiary_info: BeneficiaryInfo[];
      }> {
        return products.flatMap(product =>
          product.policies_or_accounts.flatMap(policy_or_account => ({
            product_type: product.details.product_type, // המרת מספר למחרוזת
            policy_number: policy_or_account.policy_number, // מספר פוליסה
            policy_name: policy_or_account.program_name, // שם תוכנית
            beneficiary_info: policy_or_account.insurence_coverages?.flatMap(coverage => coverage.beneficiaries) || []
          }))
        );
      }
      static filterComprehensivePensionPlans(policies: PolicyOrAccount[]): PolicyOrAccount[] {
        return policies.filter(
          (policy) => policy.pension_data?.pension_plan_type === PensionPlanType.COMPREHENSIVE
        );
      }

      static extractOccupationsFromProducts(products: Product[]): OccupationInfo[] {
        return products.flatMap(product =>
          product.policies_or_accounts.flatMap((policy: PolicyOrAccount) =>
            policy.insurence_coverages?.flatMap((coverage: InsuranceCoverage) =>
              coverage.occupation_info.filter(occupation =>
                Object.values(occupation).some(value => value !== "")
              )
            ) || []
          )
        );
      }
      /**
       * Guaranteed conversion factors of one policy that are really guaranteed — a reported factor counts only when
       * it is set and life_expectancy_guaranteed is not 2 (which makes it not guaranteed).
       */
      static getGuaranteedConversionFactorsOfPolicy(policyOrAccount: PolicyOrAccount): number[] {
          const factors = new Set<number>();
          (policyOrAccount.retirement_savings_by_age ?? []).forEach(savings => {
            const factor = savings.guaranteed_conversion_factor;
            const lifeExp = savings.life_expectancy_guaranteed;

            if (
              factor !== undefined &&
              factor !== 0 &&
              lifeExp !== 2
            ) {
              factors.add(factor);
            }
          });
          return Array.from(factors);
        }

      static getUniqueGuaranteedConversionFactors(products: Product[]): number[] {
          const factors = new Set<number>();

          products.forEach(product => {
            product.policies_or_accounts.forEach(policyOrAccount => {
              MimshakPensionService.getGuaranteedConversionFactorsOfPolicy(policyOrAccount).forEach(factor => factors.add(factor));
            });
          });

          return Array.from(factors);
        }
      /**
       * Data-integrity check for one policy: the producer reports a projected retirement amount (without premiums)
       * while the current balance is 0 — a rare producer-data bug; the projection should not be trusted.
       */
      static hasProjectionWithoutBalance(policy: PolicyOrAccount): boolean {
        const projectedAmount = MimshakPensionService.aggregatePensionFundDetails(policy)?.total_accumulated_amount_without_premiums || 0;
        return projectedAmount > 0 && (MimshakService.calculatePolicyTotals(policy) || 0) === 0;
      }

      /** Policies failing hasProjectionWithoutBalance, with the details shown in the warning tooltip */
      static getPoliciesWithProjectionWithoutBalance(
        products: Product[]
      ): {
        policyNumber: string;
        projectedAmount: number;
        productType?: number;
        handlerCode?: string;
        clientName?: string;
        updateDate?: string;
      }[] {
        return products.flatMap((product) =>
          product.policies_or_accounts
            .filter((policy) => MimshakPensionService.hasProjectionWithoutBalance(policy))
            .map((policy) => ({
              policyNumber: policy.policy_number,
              productType: product.details?.adjusted_product_type ?? product.details?.product_type,
              handlerCode: product.details?.handler_code,
              clientName: [product.client?.client_name, product.client?.last_name].filter(Boolean).join(" ") || undefined,
              updateDate: policy.update_date,
              projectedAmount:
                MimshakPensionService.aggregatePensionFundDetails(policy)?.total_accumulated_amount_without_premiums || 0,
            }))
        );
      }

      static aggregatePensionFundDetails(policy: PolicyOrAccount): AggregatedPensionFundDetails {
        if (!policy.pension_fund_details || policy.pension_fund_details.length === 0) {
          return null;
        }

        if (policy.pension_fund_details.length === 1) {
          const d = policy.pension_fund_details[0];

          return {
            retirement_pension_amount: d.retirement_pension_amount || d.expected_monthly_pension,
            expected_monthly_pension: d.expected_monthly_pension,
            total_expected_balance_with_premiums: d.total_expected_balance_with_premiums || d.expected_savings_without_premiums,
            expected_savings_without_premiums: d.expected_savings_without_premiums,
            total_accumulated_amount_with_premiums: d.total_accumulated_amount_with_premiums || d.total_accumulated_amount_without_premiums,
            total_accumulated_amount_without_premiums: d.total_accumulated_amount_without_premiums,
            forecast_yield_percentage_range: `${d.forecast_yield_percentage || "-"}`,
            fund_type: d.fund_type ? [d.fund_type] : [],
          };
        }

        // בדיקה האם יש בדיוק 11 שורות PAYING - אם כן, נבחר את השורה השמינית
        const payingFunds = policy.pension_fund_details.filter(detail => detail.fund_type === PensionFundType.PAYING);
        if (payingFunds.length === 11 || payingFunds.length === 8) {
          const d = payingFunds[7]; // השורה השמינית (אינדקס 7)

          return {
            retirement_pension_amount: d.retirement_pension_amount || d.expected_monthly_pension,
            expected_monthly_pension: d.expected_monthly_pension,
            total_expected_balance_with_premiums: d.total_expected_balance_with_premiums || d.expected_savings_without_premiums,
            expected_savings_without_premiums: d.expected_savings_without_premiums,
            total_accumulated_amount_with_premiums: d.total_accumulated_amount_with_premiums || d.total_accumulated_amount_without_premiums,
            total_accumulated_amount_without_premiums: d.total_accumulated_amount_without_premiums,
            forecast_yield_percentage_range: d.forecast_yield_percentage ? `${d.forecast_yield_percentage}%` : "-",
            fund_type: d.fund_type ? [d.fund_type] : [],
          };
        }
    
         // ✨ הצהרת טיפוס עבור `aggregated`
     // ✨ הצהרת טיפוס עבור `aggregated`
     const aggregated: AggregatedPensionFundDetails = policy.pension_fund_details.reduce(
      (acc: AggregatedPensionFundDetails, d) => {
        acc.retirement_pension_amount += d.retirement_pension_amount || d.expected_monthly_pension || 0;
        acc.expected_monthly_pension += d.expected_monthly_pension || 0;
        acc.total_expected_balance_with_premiums += d.total_expected_balance_with_premiums || d.expected_savings_without_premiums || 0;
        acc.expected_savings_without_premiums += d.expected_savings_without_premiums || 0;
        acc.total_accumulated_amount_with_premiums += d.total_accumulated_amount_with_premiums || d.total_accumulated_amount_without_premiums || 0;
        acc.total_accumulated_amount_without_premiums += d.total_accumulated_amount_without_premiums || 0;
        return acc;
      },
      {
        retirement_pension_amount: 0,
        expected_monthly_pension: 0,
        total_expected_balance_with_premiums: 0,
        expected_savings_without_premiums: 0,
        total_accumulated_amount_with_premiums: 0,
        total_accumulated_amount_without_premiums: 0,
        forecast_yield_percentage_range: "-",
        fund_type: [],
      }
    );

    // ✨ 2. יצירת טווח ערכים עבור `forecast_yield_percentage`
    const uniqueYieldValues = Array.from(new Set(
      policy.pension_fund_details
        .map(d => d.forecast_yield_percentage)
        .filter((v): v is number => typeof v === "number" && Number.isFinite(v) && v !== 0)
    ));
    
    if (uniqueYieldValues.length > 1) {
      // ✨ 1. המרת המספרים ל- `number` ואז הוספת `%` ידנית
      const minYield = Math.min(...uniqueYieldValues);
      const maxYield = Math.max(...uniqueYieldValues);
      aggregated.forecast_yield_percentage_range = `${minYield}% - ${maxYield}%`;
    } else {
      // ✨ 2. אם יש רק ערך אחד, מחזירים אותו עם `%` בודד
      aggregated.forecast_yield_percentage_range = uniqueYieldValues.length ? `${uniqueYieldValues[0]}%` : "-";
    }

    // ✨ 3. מציאת כל ה- `fund_type` הייחודיים
    aggregated.fund_type = Array.from(new Set(policy.pension_fund_details.map(d => d.fund_type).filter(Boolean)));

    return aggregated;
  }
static calculateTotalTaxReportedDepositsFromPolicies = (
  policies: PolicyOrAccount[]
): number => {
 return policies.reduce((sum, policy) => {
    const yearSummaries = policy.year_deposit_summary ?? [];
    const yearlySum = yearSummaries.reduce((acc, y) => {
      return acc +
        (y.total_employee_contributions ?? 0) +
        (y.total_employer_contributions ?? 0) +
        (y.total_compensation_contributions ?? 0);
    }, 0);
    return sum + yearlySum;
  }, 0);
};

  static aggregatePensionSummary(products: Product[]): AggregatedPensionFundDetails {
    let aggregated: AggregatedPensionFundDetails = {
      total_accumulated_amount_without_premiums: 0,
      total_accumulated_amount_with_premiums: 0,
      retirement_pension_amount: 0,
      expected_monthly_pension: 0,
      total_expected_balance_with_premiums: 0,
      expected_savings_without_premiums: 0,
      forecast_yield_percentage_range: "-",
      fund_type: [],
    };

    let yieldValues: number[] = [];

    products.forEach((product) => {
      product.policies_or_accounts.forEach((policy) => {
        const policyAggregated = MimshakPensionService.aggregatePensionFundDetails(policy);
        if (!policyAggregated) return;
        const withPremiums = policyAggregated.total_accumulated_amount_with_premiums || 0;
        const withoutPremiums = policyAggregated.total_accumulated_amount_without_premiums || 0;
        const retirementPension = policyAggregated.retirement_pension_amount || 0;
        const expectedPension = policyAggregated.expected_monthly_pension || 0;
        const balanceWithPremiums = policyAggregated.total_expected_balance_with_premiums || 0;
        const savingsWithoutPremiums = policyAggregated.expected_savings_without_premiums || 0;

        aggregated.total_accumulated_amount_without_premiums += withoutPremiums;
        aggregated.total_accumulated_amount_with_premiums += withPremiums || withoutPremiums;
        aggregated.retirement_pension_amount += retirementPension || expectedPension;
        aggregated.expected_monthly_pension += expectedPension;
        aggregated.total_expected_balance_with_premiums += balanceWithPremiums || savingsWithoutPremiums;
        aggregated.expected_savings_without_premiums += savingsWithoutPremiums;

        yieldValues.push(...MimshakPensionService.parseYieldRange(policyAggregated.forecast_yield_percentage_range));

        // הוספת כל סוגי הקרנות הייחודיים למערך
        aggregated.fund_type = Array.from(new Set([...aggregated.fund_type, ...policyAggregated.fund_type]));
      });
    });

    // חישוב טווח `forecast_yield_percentage`
    aggregated.forecast_yield_percentage_range = MimshakPensionService.formatYieldRange(yieldValues);

    return aggregated;
  }

  /**
   * Numbers in a forecast-yield range string ("3.74%", "3% - 4%", "-"), keeping their sign.
   * Splitting on "-" used to turn "-" (no forecast) into NaN — "NaN% - NaN%" — and break negative yields.
   * 0 means "not reported", not a 0% forecast, so it is ignored as well.
   */
  static parseYieldRange(range: string | null | undefined): number[] {
    return (range?.match(/-?\d+(?:\.\d+)?/g) ?? []).map(Number).filter((v) => Number.isFinite(v) && v !== 0);
  }

  /** "min% - max%", "x%" when all equal, or "-" when there are no values */
  static formatYieldRange(values: number[]): string {
    if (values.length === 0) return "-";
    const minYield = Math.min(...values);
    const maxYield = Math.max(...values);
    return minYield === maxYield ? `${minYield}%` : `${minYield}% - ${maxYield}%`;
  }

  /**
   * Forecast-yield range of only the policies that took part in a given figure — e.g. those that report a
   * projected annuity amount, or a projected capital amount — rather than of every policy.
   */
  static getYieldRangeOfContributingPolicies(
    products: Product[],
    contributes: (policyAggregated: AggregatedPensionFundDetails) => boolean
  ): string {
    const values = products.flatMap((product) =>
      product.policies_or_accounts.flatMap((policy) => {
        const policyAggregated = MimshakPensionService.aggregatePensionFundDetails(policy);
        return policyAggregated && contributes(policyAggregated)
          ? MimshakPensionService.parseYieldRange(policyAggregated.forecast_yield_percentage_range)
          : [];
      })
    );
    return MimshakPensionService.formatYieldRange(values);
  }

  /**
   * סיכום פנסיה מחולק לפי סוג קרן (PAYING, NON_PAYING, CAPITAL)
   * @param products - מוצרי הפנסיה
   * @returns אובייקט עם שלושה סיכומים נפרדים
   */
  static aggregatePensionSummaryByFundType(products: Product[]): {
    paying: AggregatedPensionFundDetails;
    nonPaying: AggregatedPensionFundDetails;
    capital: AggregatedPensionFundDetails;
  } {
    const createEmptyAggregated = (): AggregatedPensionFundDetails => ({
      total_accumulated_amount_without_premiums: 0,
      total_accumulated_amount_with_premiums: 0,
      retirement_pension_amount: 0,
      expected_monthly_pension: 0,
      total_expected_balance_with_premiums: 0,
      expected_savings_without_premiums: 0,
      forecast_yield_percentage_range: "-",
      fund_type: [],
    });

    const paying = createEmptyAggregated();
    const nonPaying = createEmptyAggregated();
    const capital = createEmptyAggregated();

    const payingYieldValues: number[] = [];
    const nonPayingYieldValues: number[] = [];
    const capitalYieldValues: number[] = [];

    products.forEach((product) => {
      product.policies_or_accounts.forEach((policy) => {
        if (!policy.pension_fund_details || policy.pension_fund_details.length === 0) {
          return;
        }

        // חלוקה לפי fund_type
        const payingDetails = policy.pension_fund_details.filter(d => d.fund_type === PensionFundType.PAYING);
        const nonPayingDetails = policy.pension_fund_details.filter(d => d.fund_type === PensionFundType.NON_PAYING);
        const capitalDetails = policy.pension_fund_details.filter(d => d.fund_type === PensionFundType.TRAINING_FUND);

        // פונקציה עזר לסיכום פרטים
        const aggregateDetails = (
          details: PensionFundDetails[],
          targetAggregated: AggregatedPensionFundDetails,
          yieldValues: number[]
        ) => {
          details.forEach((detail) => {
            targetAggregated.total_accumulated_amount_without_premiums += detail.total_accumulated_amount_without_premiums || 0;
            targetAggregated.total_accumulated_amount_with_premiums += detail.total_accumulated_amount_with_premiums || 0;
            targetAggregated.retirement_pension_amount += detail.retirement_pension_amount || 0;
            targetAggregated.expected_monthly_pension += detail.expected_monthly_pension || 0;
            targetAggregated.total_expected_balance_with_premiums += detail.total_expected_balance_with_premiums || 0;
            targetAggregated.expected_savings_without_premiums += detail.expected_savings_without_premiums || 0;

            if (
              typeof detail.forecast_yield_percentage === "number" &&
              Number.isFinite(detail.forecast_yield_percentage) &&
              detail.forecast_yield_percentage !== 0
            ) {
              yieldValues.push(detail.forecast_yield_percentage);
            }

            if (detail.fund_type !== undefined && !targetAggregated.fund_type.includes(detail.fund_type)) {
              targetAggregated.fund_type.push(detail.fund_type);
            }
          });
        };

        aggregateDetails(payingDetails, paying, payingYieldValues);
        aggregateDetails(nonPayingDetails, nonPaying, nonPayingYieldValues);
        aggregateDetails(capitalDetails, capital, capitalYieldValues);
      });
    });

    // חישוב טווחי תשואה
    const calculateYieldRange = (yieldValues: number[]): string => {
      if (yieldValues.length === 0) return "-";
      const minYield = Math.min(...yieldValues);
      const maxYield = Math.max(...yieldValues);
      return minYield === maxYield ? `${minYield}%` : `${minYield}% - ${maxYield}%`;
    };

    paying.forecast_yield_percentage_range = calculateYieldRange(payingYieldValues);
    nonPaying.forecast_yield_percentage_range = calculateYieldRange(nonPayingYieldValues);
    capital.forecast_yield_percentage_range = calculateYieldRange(capitalYieldValues);

    return { paying, nonPaying, capital };
  }
  }
  export interface AggregatedPensionFundDetails {
    retirement_pension_amount: number;
    expected_monthly_pension: number;
    total_expected_balance_with_premiums: number;
    expected_savings_without_premiums: number;
    total_accumulated_amount_with_premiums: number;
    total_accumulated_amount_without_premiums: number;
   
    forecast_yield_percentage_range: string;
    fund_type: number[];
  }
  